import { MouseEvent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { colorShade } from '../../utils/style';
import { _UITooltipController } from './TooltipController';

export interface TooltipBodyProps {
    id: number;
    children: any;
    event: MouseEvent;
    target: HTMLElement;

    originPosition?: AnchorPosition;
    anchorPosition?: AnchorPosition;
    offset?: Position;
    naked?: boolean;
    className?: string;
}

type AnchorPosition = { x: 'right' | 'left' | 'center'; y: 'top' | 'center' | 'bottom'; }
type Position = { x: number; y: number; }

function TooltipBody({ id, children, event, target, originPosition = { x: 'center', y: 'top' }, anchorPosition = { x: 'center', y: 'bottom' }, offset = { x: 0, y: 0 }, naked = false, className }: TooltipBodyProps) {
    const [closing, setClosing] = useState(false);
    const [visible, setVisible] = useState(false);
    const [childHovered, setChildHovered] = useState(true);
    const [selfHovered, setSelfHovered] = useState(false);
    const [position, setPosition] = useState<Position>(() => ({ x: 0, y: 0 }));

    const selfRef = useRef<HTMLElement>(null);

    function calcPosition() {
        const targetRect = target.getBoundingClientRect();
        const selfRect = (selfRef.current as HTMLElement).getBoundingClientRect();

        const originOffset: { [axis in keyof AnchorPosition]: { [position in AnchorPosition[axis]]: () => number } } = {
            x: { right: () => -selfRect.width, center: () => -selfRect.width / 2, left: () => 0 },
            y: { top: () => 0, center: () => -selfRect.height / 2, bottom: () => -selfRect.height }
        }

        const targetOffset: { [axis in keyof AnchorPosition]: { [position in AnchorPosition[axis]]: () => number } } = {
            x: { right: () => targetRect.width, center: () => targetRect.width / 2, left: () => 0 },
            y: { top: () => 0, center: () => targetRect.height / 2, bottom: () => targetRect.height }
        }

        let position = { x: targetRect.x + originOffset.x[originPosition.x]() + targetOffset.x[anchorPosition.x]() + offset.x, y: targetRect.y + originOffset.y[originPosition.y]() + targetOffset.y[anchorPosition.y]() + offset.y }

        const invertPositionX: { [key in AnchorPosition['x']]: AnchorPosition['x'] } = { right: 'left', center: 'center', left: 'right' };
        const invertPositionY: { [key in AnchorPosition['y']]: AnchorPosition['y'] } = { top: 'bottom', center: 'center', bottom: 'top' };

        if (((position.x + selfRect.width > window.innerWidth) && (position.x + selfRect.width / 2 > targetRect.x + targetRect.width / 2)) ||
            ((position.x < 0) && (position.x + selfRect.width / 2 < targetRect.x + targetRect.width / 2))) {
            position.x = targetRect.x + originOffset.x[invertPositionX[originPosition.x]]() + targetOffset.x[invertPositionX[anchorPosition.x]]() - offset.x;
        }

        if (((position.y + selfRect.height > window.innerHeight) && (position.y + selfRect.height / 2 > targetRect.y + targetRect.height / 2)) ||
            ((position.y < 0) && (position.y + selfRect.height / 2 < targetRect.y + targetRect.height / 2))) {
            position.y = targetRect.y + originOffset.y[invertPositionY[originPosition.y]]() + targetOffset.y[invertPositionY[anchorPosition.y]]() - offset.y;
        }

        if (position.x + selfRect.width > window.innerWidth) position.x -= (position.x + selfRect.width) - (window.innerWidth);
        if (position.x < 0) position.x = 0;

        if (position.y + selfRect.height > window.innerHeight) position.y -= (position.y + selfRect.height) - (window.innerHeight);
        if (position.y < 0) position.y = 0;

        position.y += window.scrollY;

        setPosition(position);
    }

    useLayoutEffect(() => {
        const title = target.title;
        target.removeAttribute('title');

        const onMouseEnter = () => { setChildHovered(true) };
        const onMouseLeave = () => { setChildHovered(false) };

        target.addEventListener('mouseenter', onMouseEnter);
        target.addEventListener('mouseleave', onMouseLeave);

        calcPosition();

        return () => {
            if (title) target.title = title;
            target.removeEventListener('mouseenter', onMouseEnter);
            target.removeEventListener('mouseleave', onMouseLeave);
        };
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        let timeout: NodeJS.Timeout;

        const shouldStayOpened = (!closing && selfHovered) || childHovered;

        if (shouldStayOpened) {
            setVisible(true);
            setClosing(false);
        }
        else {
            timeout = setTimeout(() => _UITooltipController.remove(id), 200);
            setClosing(true);
        }

        return () => clearTimeout(timeout);
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [childHovered, selfHovered])

    return (
        <Wrapper className={`${className || ''} ${naked ? 'naked' : ''}`} ref={selfRef} onClick={e => e.stopPropagation()} hovered={((!closing && selfHovered) || childHovered) && visible} onMouseEnter={() => setSelfHovered(true)} onMouseLeave={() => setSelfHovered(false)} style={{ top: position.y, left: position.x }}>
            <div className='tooltip'>{typeof children === 'function' ? children() : children}</div>

        </Wrapper>
    )
}

export default TooltipBody;

const Wrapper = styled.div<{ hovered: boolean, ref: any }>`
    padding: 10px;
    position: absolute;

    .tooltip {
        background-color: ${props => colorShade(props.theme.backgroundColor, 0.2)};
        border-radius: ${props => props.theme.borderRadius};
        color: #ffffff;
        font-size: 12px;
        font-weight: 500;
        padding: 10px 14px;
        transition: transform var(--ui-animation-duration), opacity var(--ui-animation-duration);
        transform: scale(0); opacity: 0;

        ${({ hovered }) => hovered && css`transform: scale(1); opacity: 1;`}
    }

    &.naked .tooltip { background-color: unset; border-radius: unset; padding: unset }
`